import type { ComponentType } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MetricItemDTO } from '@/types/agentEvents';
import { MetricsBarChart } from './MetricsBarChart';
import type { MetricsBarChartProps } from './MetricsBarChart';

export interface IntervalMetricsChartWithSeriesProps extends Omit<
  MetricsBarChartProps,
  'data'
> {
  /** Full interval data (may contain multiple series: agents, queues, etc.). */
  data: MetricItemDTO[];
  /** Grouping dimension key (e.g. AGENT, QUEUE) used to split series. */
  groupingKey: string;
  /** Override default bar chart (e.g. line chart for service level). */
  IntervalChartComponent?: ComponentType<MetricsBarChartProps>;
}

function getSeriesValue(item: MetricItemDTO, groupingKey: string): string {
  return (
    item.dimensions?.[groupingKey] ?? item.dimensionLabels?.[groupingKey] ?? ''
  );
}

function getSeriesLabel(item: MetricItemDTO, groupingKey: string): string {
  return (
    item.dimensionLabels?.[groupingKey] ?? item.dimensions?.[groupingKey] ?? ''
  );
}

/** Extracts unique series (e.g. agents, queues) from interval data for the dropdown. */
function getSeriesOptions(
  data: MetricItemDTO[],
  groupingKey: string
): { value: string; label: string }[] {
  const seen = new Set<string>();
  const options: { value: string; label: string }[] = [];
  for (const item of data) {
    const value = getSeriesValue(item, groupingKey);
    if (value === '') continue;
    if (seen.has(value)) continue;
    seen.add(value);
    options.push({
      value,
      label: getSeriesLabel(item, groupingKey) || value,
    });
  }
  return options;
}

export function IntervalMetricsChartWithSeries({
  data,
  groupingKey,
  IntervalChartComponent = MetricsBarChart,
  ...chartProps
}: IntervalMetricsChartWithSeriesProps) {
  const { t } = useTranslation();
  const [selectedSeriesValue, setSelectedSeriesValue] = useState<string | null>(
    null
  );

  const seriesOptions = useMemo(
    () => getSeriesOptions(data, groupingKey),
    [data, groupingKey]
  );

  useEffect(() => {
    if (seriesOptions.length === 0) return;
    const currentValid = seriesOptions.some(
      o => o.value === selectedSeriesValue
    );
    if (!currentValid) {
      setSelectedSeriesValue(seriesOptions[0].value);
    }
  }, [seriesOptions, selectedSeriesValue]);

  const effectiveSeriesValue =
    selectedSeriesValue ?? seriesOptions[0]?.value ?? null;

  const filteredData = useMemo(() => {
    if (effectiveSeriesValue == null) return [];
    return data.filter(
      item => getSeriesValue(item, groupingKey) === effectiveSeriesValue
    );
  }, [data, groupingKey, effectiveSeriesValue]);

  if (seriesOptions.length === 0) {
    return <IntervalChartComponent data={[]} {...chartProps} />;
  }

  const selectedValue = effectiveSeriesValue ?? seriesOptions[0].value;

  return (
    <div className="w-full space-y-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
        <label className="text-sm text-gray-600 whitespace-nowrap">
          {t('components.dashboard.viewBySeries')}
        </label>
        <select
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[200px]"
          value={selectedValue}
          onChange={e => setSelectedSeriesValue(e.target.value)}
        >
          {seriesOptions.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      <IntervalChartComponent data={filteredData} {...chartProps} />
    </div>
  );
}
